import { View, Text, styled } from 'tamagui'
import { useServerStore } from '@/store'
import { Users } from '@tamagui/lucide-icons'
import { Badge, TierBadge } from '../../common'

const Card = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 16,
  cursor: 'pointer',
  hoverStyle: {
    borderColor: '$accent',
  },
  // @ts-expect-error -- web transition
  style: {
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  },
})

const IconRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  marginBottom: 12,
})

const IconCircle = styled(View, {
  width: 44,
  height: 44,
  borderRadius: '$borderRadiusXl',
  alignItems: 'center',
  justifyContent: 'center',
})

const ServerName = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 16,
  color: '$surfaceForeground',
  flex: 1,
})

const MetaRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
})

const MetaText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$sidebarMuted',
})

interface ServerCardProps {
  serverId: string
  onPress: () => void
}

export function ServerCard({ serverId, onPress }: ServerCardProps) {
  const server = useServerStore((s) => s.servers[serverId])
  if (!server) return null

  const textChannels = server.channels.filter(c => c.type === 'text')
  const lastActivity = textChannels.find(c => c.unreadCount > 0)

  return (
    <Card
      onPress={onPress}
      {...(server.unreadCount > 0 && {
        // @ts-expect-error -- web box-shadow
        style: {
          borderColor: `${server.iconColor}44`,
          boxShadow: `0 0 16px ${server.iconColor}11`,
          transition: 'border-color 150ms ease, box-shadow 150ms ease',
        },
      })}
    >
      <IconRow>
        <IconCircle backgroundColor={server.iconColor}>
          <Text fontFamily="$fontHeading" fontWeight="600" fontSize={16} color="$surface">
            {server.iconText}
          </Text>
        </IconCircle>
        <ServerName numberOfLines={1}>{server.name}</ServerName>
        <TierBadge tier={server.tier} size="sm" />
        {server.unreadCount > 0 && (
          <Badge count={server.unreadCount} variant={server.mentionCount > 0 ? 'mention' : 'unread'} />
        )}
      </IconRow>

      <MetaRow>
        <View flexDirection="row" alignItems="center" gap={4}>
          <Users size={12} color="$sidebarMuted" />
          <MetaText>{server.memberCount.toLocaleString()} members</MetaText>
        </View>
        {lastActivity && (
          <MetaText>#{lastActivity.name} active</MetaText>
        )}
      </MetaRow>
    </Card>
  )
}
